// src/components/ManageAdmin/ManageAdmin.js
import React, { useState, useEffect } from 'react';
import './ManageAdmin.css';
import collegeConfig from '../../config/collegeConfig';
import {
  FaEdit,
  FaTrash,
  FaEye,
  FaPhone,
  FaSearch,
  FaTimes,
  FaSave,
  FaKey,
  FaUserShield,
  FaBuilding,
  FaMobileAlt,
  FaLock,
  FaUnlock,
  FaExclamationTriangle,
  FaCheckCircle
} from 'react-icons/fa';

const ManageAdmin = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'view', 'password'
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    department: '',
    phone: '',
    mobile: ''
  });
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchAdmins();
    getCurrentUser();
  }, []);

  const getCurrentUser = () => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      setCurrentUser(user);
    }
  };

  const fetchAdmins = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/admins`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAdmins(data.admins || []);
      } else {
        setError('Failed to fetch admins');
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const url = modalMode === 'create' 
        ? `${collegeConfig.api.baseURL}/admins`
        : `${collegeConfig.api.baseURL}/admins/${selectedAdmin._id}`;
      
      const method = modalMode === 'create' ? 'POST' : 'PUT';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess(data.message);
        fetchAdmins();
        closeModal();
      } else {
        setError(data.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Error saving admin:', error);
      setError('Error connecting to server');
    }
  };

  const handleDelete = async (adminId) => {
    // Check if trying to delete own account
    if (currentUser && (adminId === currentUser.id || adminId === currentUser._id)) {
      setError('You cannot delete your own admin account');
      return;
    }
    
    if (!window.confirm('Are you sure you want to deactivate this admin account?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/admins/${adminId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess(data.message);
        fetchAdmins();
      } else {
        setError(data.message);
      }
    } catch (error) {
      console.error('Error deleting admin:', error);
      setError('Error connecting to server');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate passwords match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    
    // Validate password length
    if (passwordData.newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    // Check if changing own password
    const isOwnAccount = currentUser && 
      (selectedAdmin._id === currentUser.id || selectedAdmin._id === currentUser._id);
    
    // If changing own password, current password is required
    if (isOwnAccount && !passwordData.currentPassword) {
      setError('Current password is required');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${collegeConfig.api.baseURL}/admins/${selectedAdmin._id}/change-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            currentPassword: passwordData.currentPassword,
            newPassword: passwordData.newPassword
          })
        }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess(data.message);
        closeModal();
        
        // If changed own password, show special message
        if (isOwnAccount) {
          setSuccess('Your password has been changed successfully. Please remember your new password.');
        }
      } else {
        setError(data.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setError('Error connecting to server');
    }
  };

  const openModal = (mode, admin = null) => {
    setModalMode(mode);
    setSelectedAdmin(admin);
    setError('');
    
    if (mode === 'create') {
      setFormData({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        department: '',
        phone: '',
        mobile: ''
      });
    } else if (admin && mode !== 'password') {
      setFormData({
        email: admin.email,
        password: '', // Don't populate password for edit
        firstName: admin.profile.firstName,
        lastName: admin.profile.lastName,
        department: admin.profile.department || '',
        phone: admin.profile.phone || '',
        mobile: admin.profile.mobile || ''
      });
    }
    
    if (mode === 'password') {
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    }
    
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedAdmin(null);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setError('');
  };

  // Check if admin is current user
  const isCurrentUser = (adminId) => {
    return currentUser && (adminId === currentUser.id || adminId === currentUser._id);
  };

  // Filter admins based on search
  const filteredAdmins = admins.filter(admin => {
    const fullName = `${admin.profile.firstName} ${admin.profile.lastName}`.toLowerCase();
    const email = admin.email.toLowerCase();
    const searchLower = searchTerm.toLowerCase();
    return fullName.includes(searchLower) || email.includes(searchLower);
  });

  // Clear success message after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  if (loading) {
    return (
      <div className="manage-admin-loading">
        <div className="spinner"></div>
        <p>Loading administrators...</p>
      </div>
    );
  }

  return (
    <div className="manage-admin-container">
      {/* Header Section */}
      <div className="manage-admin-header">
        <div className="header-content">
          <h2>Manage Administrators</h2>
          <p>Manage system administrator accounts and permissions</p>
        </div>
        <button 
          className="btn-create-admin"
          onClick={() => openModal('create')}
        >
          <FaUserShield /> Add New Admin
        </button>
      </div>

      {/* Success/Error Messages */}
      {success && <div className="alert alert-success"><FaCheckCircle /> {success}</div>}
      {error && <div className="alert alert-error"><FaExclamationTriangle /> {error}</div>}

      {/* Search Bar */}
      <div className="admin-controls">
        <div className="search-bar">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search administrators by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="admin-stats">
          <div className="stat-badge">
            <FaUserShield />
            <span>{admins.length} Total Admins</span>
          </div>
          <div className="stat-badge">
            <FaCheckCircle />
            <span>{admins.filter(a => a.isActive).length} Active</span>
          </div>
        </div>
      </div>

      {/* Admins Grid */}
      <div className="admins-grid">
        {filteredAdmins.length === 0 ? (
          <div className="no-admins">
            <FaUserShield className="no-admins-icon" />
            <h3>No administrators found</h3>
            <p>Add administrators to manage the system</p>
          </div>
        ) : (
          filteredAdmins.map(admin => (
            <div key={admin._id} className={`admin-card ${isCurrentUser(admin._id) ? 'current-user' : ''}`}>
              {isCurrentUser(admin._id) && (
                <div className="current-user-badge">Your Account</div>
              )}
              <div className="admin-card-header">
                <div className="admin-avatar">
                  <FaUserShield />
                </div>
                <div className="admin-info">
                  <h3>{admin.profile.firstName} {admin.profile.lastName}</h3>
                  <p className="admin-email">{admin.email}</p>
                </div>
                <div className="admin-actions">
                  <button 
                    className="btn-icon btn-view"
                    onClick={() => openModal('view', admin)}
                    title="View Details"
                  >
                    <FaEye />
                  </button>
                  <button 
                    className="btn-icon btn-edit"
                    onClick={() => openModal('edit', admin)}
                    title="Edit Admin"
                  >
                    <FaEdit />
                  </button>
                  <button 
                    className="btn-icon btn-password"
                    onClick={() => openModal('password', admin)}
                    title="Change Password"
                  >
                    <FaKey />
                  </button>
                  {!isCurrentUser(admin._id) && (
                    <button 
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(admin._id)}
                      title="Delete Admin"
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
              </div>
              
              <div className="admin-card-body">
                <div className="admin-details">
                  {admin.profile.department && (
                    <div className="detail-item">
                      <FaBuilding className="detail-icon" />
                      <span>{admin.profile.department}</span>
                    </div>
                  )}
                  {admin.profile.phone && (
                    <div className="detail-item">
                      <FaPhone className="detail-icon" />
                      <span>{admin.profile.phone}</span>
                    </div>
                  )}
                  {admin.profile.mobile && (
                    <div className="detail-item">
                      <FaMobileAlt className="detail-icon" />
                      <span>{admin.profile.mobile}</span>
                    </div>
                  )}
                </div>
                
                <div className="admin-status">
                  <span className={`status-badge ${admin.isActive ? 'active' : 'inactive'}`}>
                    {admin.isActive ? (
                      <><FaUnlock /> Active</>
                    ) : (
                      <><FaLock /> Inactive</>
                    )}
                  </span>
                  <span className="created-date">
                    Added: {new Date(admin.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {modalMode === 'create' ? 'Add New Administrator' : 
                 modalMode === 'edit' ? 'Edit Administrator' : 
                 modalMode === 'password' ? 'Change Password' : 'Administrator Details'}
              </h3>
              <button className="modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>
            
            {modalMode === 'password' ? (
              <form onSubmit={handlePasswordChange} className="password-form">
                {isCurrentUser(selectedAdmin._id) && (
                  <div className="form-group">
                    <label>Current Password *</label>
                    <input
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                      placeholder="Enter your current password"
                      required
                    />
                  </div>
                )}
                
                <div className="form-group">
                  <label>New Password *</label>
                  <input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                    placeholder="Minimum 6 characters"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Confirm New Password *</label>
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                    placeholder="Re-enter new password"
                    required
                  />
                </div>
                
                {!isCurrentUser(selectedAdmin._id) && (
                  <div className="info-message">
                    <FaExclamationTriangle />
                    You are changing the password for {selectedAdmin.profile.firstName} {selectedAdmin.profile.lastName}
                  </div>
                )}
                
                <div className="form-actions">
                  <button type="submit" className="btn-primary">
                    <FaKey /> Change Password
                  </button>
                  <button type="button" className="btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                </div>
              </form>
            ) : modalMode !== 'view' ? (
              <form onSubmit={handleSubmit} className="admin-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                    />
                  </div>
                  
                  {modalMode === 'create' && (
                    <div className="form-group">
                      <label>Password *</label>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        placeholder="Minimum 6 characters"
                        required
                      />
                    </div>
                  )}
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Department</label>
                    <input
                      type="text"
                      value={formData.department}
                      onChange={(e) => setFormData({...formData, department: e.target.value})}
                      placeholder="e.g., Administration"
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="Landline number"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Mobile</label>
                    <input
                      type="tel"
                      value={formData.mobile}
                      onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                      placeholder="Mobile number"
                    />
                  </div>
                </div>
                
                <div className="form-actions">
                  <button type="submit" className="btn-primary">
                    <FaSave /> {modalMode === 'create' ? 'Create Administrator' : 'Update Administrator'}
                  </button>
                  <button type="button" className="btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="view-info">
                <div className="info-section">
                  <h4>Account Information</h4>
                  <p><strong>Name:</strong> {selectedAdmin.profile.firstName} {selectedAdmin.profile.lastName}</p>
                  <p><strong>Email:</strong> {selectedAdmin.email}</p>
                  <p><strong>Department:</strong> {selectedAdmin.profile.department || 'Not specified'}</p>
                  <p><strong>Status:</strong> <span className={`status-text ${selectedAdmin.isActive ? 'active' : 'inactive'}`}>
                    {selectedAdmin.isActive ? 'Active' : 'Inactive'}
                  </span></p>
                  <p><strong>Created:</strong> {new Date(selectedAdmin.createdAt).toLocaleDateString()}</p>
                </div>
                
                {(selectedAdmin.profile.phone || selectedAdmin.profile.mobile) && (
                  <div className="info-section">
                    <h4>Contact Information</h4>
                    {selectedAdmin.profile.phone && (
                      <p><strong>Phone:</strong> {selectedAdmin.profile.phone}</p>
                    )}
                    {selectedAdmin.profile.mobile && (
                      <p><strong>Mobile:</strong> {selectedAdmin.profile.mobile}</p>
                    )}
                  </div>
                )}
                
                {isCurrentUser(selectedAdmin._id) && (
                  <div className="info-section current-user-info">
                    <h4>This is Your Account</h4>
                    <p>You are currently logged in with this administrator account.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageAdmin;